import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal, { Eyebrow } from "./Reveal";
import { Wash, Sprig } from "./Watercolor";
import { SKY_IMG } from "./PaletteBand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-2xl border-[1.5px] border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-ink/35 outline-none transition focus:border-terracotta focus:ring-2 focus:ring-terracotta/25";

export default function OrderSection({ products, intent, customSpec, onIntentChange }) {
  const [form, setForm] = useState({ name: "", email: "", quantity: 1, monogram: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (customSpec) {
      setForm((f) => (f.message ? f : { ...f, message: customSpec }));
    }
  }, [customSpec]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/orders`, {
        ...form,
        quantity: Number(form.quantity),
        umbrella: intent,
      });
      toast.success("Petal received!", {
        description: "Ella will be in touch soon to arrange payment.",
      });
      setForm({ name: "", email: "", quantity: 1, monogram: "", message: "" });
      onIntentChange("");
    } catch (err) {
      toast.error("Hmm, that didn't send.", {
        description: "Please check your details and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="order" className="relative py-24 sm:py-28" data-testid="order-section">
      <Wash color="#FFD166" size={520} opacity={0.38} className="-top-16 -left-40" />
      <Wash color="#F9C5D5" size={400} opacity={0.28} className="bottom-10 -right-32" />
      <Sprig color="#F472B6" className="hidden lg:block top-32 left-[2%] w-24" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-2 lg:gap-16 items-start">
        <Reveal>
          <Eyebrow>Order Enquiry</Eyebrow>
          <h2 className="mt-4 font-serif font-medium tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl">
            Commission yours.
          </h2>
          <p className="mt-5 max-w-md text-ink/70 leading-relaxed" data-testid="order-intro">
            Tell Ella what you're dreaming of — she paints it herself, and she'll contact
            you for payment when your parasol is ready.
          </p>
          <img
            src={SKY_IMG}
            alt="A woman beneath a white umbrella under a pink evening sky"
            loading="lazy"
            className="mb-8 h-56 w-full rounded-3xl object-cover sm:h-64"
            data-testid="order-sky-image"
          />
          <ol className="mt-8 space-y-4 max-w-md">
            {[
              ["01", "Tell Ella your pick — or send your idea."],
              ["02", "She answers every note herself."],
              ["03", "She'll contact you for payment, then paint. Rain, you're on."],
            ].map(([n, t]) => (
              <li key={n} className="flex items-start gap-4" data-testid={`order-step-${n}`}>
                <span className="font-mono text-sm text-terracotta pt-1">{n}</span>
                <span className="text-ink/80">{t}</span>
              </li>
            ))}
          </ol>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            className="rounded-3xl border border-ink/10 bg-white p-6 sm:p-8 shadow-xl shadow-terracotta/5"
            data-testid="order-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="of-name" className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-ink/60">
                  Your name
                </label>
                <input
                  id="of-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Wren Bloom"
                  className={inputCls}
                  data-testid="order-name-input"
                />
              </div>
              <div>
                <label htmlFor="of-email" className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-ink/60">
                  Email
                </label>
                <input
                  id="of-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                  data-testid="order-email-input"
                />
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label htmlFor="of-umbrella" className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-ink/60">
                  Your umbrella
                </label>
                <select
                  id="of-umbrella"
                  required
                  value={intent}
                  onChange={(e) => onIntentChange(e.target.value)}
                  className={`${inputCls} appearance-none`}
                  data-testid="order-umbrella-select"
                >
                  <option value="" disabled>
                    Choose your umbrella…
                  </option>
                  {products.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name} — ${p.price}
                    </option>
                  ))}
                  <option value="Something fully custom">Something fully custom</option>
                </select>
              </div>
              <div>
                <label htmlFor="of-qty" className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-ink/60">
                  Quantity
                </label>
                <input
                  id="of-qty"
                  type="number"
                  min="1"
                  max="24"
                  required
                  value={form.quantity}
                  onChange={set("quantity")}
                  className={inputCls}
                  data-testid="order-quantity-input"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="of-monogram" className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-ink/60">
                Monogram <span className="normal-case text-ink/40">(optional, up to 8 letters)</span>
              </label>
              <input
                id="of-monogram"
                maxLength={8}
                value={form.monogram}
                onChange={set("monogram")}
                placeholder="W.B."
                className={inputCls}
                data-testid="order-monogram-input"
              />
            </div>

            <div className="mt-4">
              <label htmlFor="of-msg" className="mb-1.5 block font-mono text-[11px] uppercase tracking-wider text-ink/60">
                Tell us more
              </label>
              <textarea
                id="of-msg"
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder="Favourite flowers, colours, the wedding it's for…"
                className={`${inputCls} resize-none`}
                data-testid="order-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="order-submit-btn"
              className="mt-6 w-full rounded-full bg-terracotta py-4 font-semibold text-white shadow-xl shadow-terracotta/25 transition-all hover:bg-terracotta/90 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
            >
              {sending ? "Sending…" : "Send my enquiry"}
            </button>
            <p className="mt-3 text-center font-mono text-[11px] uppercase tracking-wider text-ink/45" data-testid="order-note">
              Fill out the form — Ella will contact you for payment
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
